import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Provider, connect, useSelector } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import { isSafari, isEdge } from "react-device-detect";
import { withTranslation } from "react-i18next";
import { WonderPush } from "react-wonderpush";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import { Toaster } from "@/src/components/ui/sonner";

import i18n, { navigatorLanguage } from "../i18n";
import { WebSocketProvider } from "../contexts/WebSocket";
import ModalConfirm from "./common/tools/ModalConfirm";
import history from "./common/navigation/AppHistory";
import Tracker from "utils/Tracker";
import Routes from "./Routes";
import { FASTProvider } from "../contexts/FASTContext";
import { UploadQueueProvider } from "../contexts/UploadQueue";
import UploadQueueToasts from "./upload/UploadQueueToasts";
import {
  setStripeKey,
  setPixelKeys,
  setStripeConnectId,
} from "../actions/architecture";
import { setLocale } from "../actions/VariableActions";
import AbilitiesProvider from "./utils/abilities/AbilitiesProvider";
import Tooltip from "./tooltip/Tooltip";
import AppMuiThemeProvider from "@/src/theme/AppMuiThemeProvider";
import { useThemeServerSync } from "@/src/hooks/useThemeServerSync";
import AdminUserAPI from "@/src/api/AdminUserAPI";
import { useQuery } from "@tanstack/react-query";

import "css/index.scss";
import "css/index.css";
import "css/admin/admin.scss";
import "css/admin/wonderpush.scss";
import "css/admin/utils/tooltip.scss";
import "css/admin/utils/item-status.scss";
import "css/admin/utils/form.scss";
import "css/admin/react-select.scss";

// TODO Sync server theme with user profile - backend needed
// function ThemeServerSync() {
//   const [hadLocal] = useState(() => localStorage.getItem("theme") !== null);

//   const isLoggedIn = useSelector((state) => state.loginReducer.isLoggedIn);
//   const profileQuery = AdminUserAPI.generateQuery({
//     fn: AdminUserAPI.getUserProfile,
//     params: [],
//     options: {
//       enabled: isLoggedIn,
//     },
//   });

//   const { data } = useQuery(profileQuery);
//   useThemeServerSync({
//     serverTheme: data?.theme,
//     hadLocal,
//   });
//   return null;
// }

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

function App(props) {
  const [defaultLng, setDefaultLng] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const {
      stripe_publishable_key,
      facebook_pixel,
      google_analytics,
      stripe_connect_client_id,
    } = props;

    props.set_stripe_publishable_key(stripe_publishable_key);
    props.set_pixel_keys(google_analytics, facebook_pixel);
    props.set_stripe_connect_client_id(stripe_connect_client_id);

    initTracker();

    if (isSafari || isEdge) setOpenModal(true);
  }, []);

  useEffect(() => {
    const { language } = props;

    if (
      (!defaultLng && !["en", "fr"].includes(navigatorLanguage)) ||
      (language && defaultLng !== language)
    ) {
      const newLng = !defaultLng && language ? language : "en";

      i18n.changeLanguage(newLng);
      props.setLocale(newLng);
      setDefaultLng(newLng);
    }
  }, [props.language]);

  const initTracker = () => {
    const { facebook_pixel, google_analytics } = props;

    const trackers = [
      {
        type: "google_analytics",
        config: { id: google_analytics },
      },
      {
        type: "facebook_pixel",
        config: { id: facebook_pixel },
      },
    ];

    Tracker.initialize(trackers);
    Tracker.sendPageView();
    history.listen(() => {
      Tracker.sendPageView();
    });
  };

  const handleClose = () => setOpenModal(false);

  const { store, t } = props;

  return (
    <>
      <Provider key="app-route-element" store={store}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <WonderPush
            options={{
              webKey:
                "e79d4d3c53ac024684a937805a1dfebb43aed5ed2849fe2695c072e873411731",
            }}
          >
            <AbilitiesProvider>
              <ConnectedRouter store={store} history={history}>
                <AppMuiThemeProvider>
                  {/* <ThemeServerSync /> */}
                  <FASTProvider>
                    <DndProvider backend={HTML5Backend}>
                      <WebSocketProvider>
                        <UploadQueueProvider>
                          <Routes />
                          <Toaster />
                          <UploadQueueToasts />
                        </UploadQueueProvider>
                      </WebSocketProvider>
                    </DndProvider>
                  </FASTProvider>
                </AppMuiThemeProvider>
              </ConnectedRouter>
            </AbilitiesProvider>
          </WonderPush>
        </QueryClientProvider>
      </Provider>
      <ModalConfirm
        open={openModal}
        onClose={handleClose}
        body={t("editor-errors:browserNotSupported")}
        cancelText={t("editor-utils:continue")}
        title={t("editor-utils:attention")}
      />
      <Tooltip id={APP_TOOLTIP_ID} />
    </>
  );
}

App.propTypes = {
  store: PropTypes.object.isRequired,
  language: PropTypes.string,
  set_stripe_publishable_key: PropTypes.func.isRequired,
  set_pixel_keys: PropTypes.func.isRequired,
  stripe_publishable_key: PropTypes.string.isRequired,
  google_analytics: PropTypes.string.isRequired,
  facebook_pixel: PropTypes.string.isRequired,
  set_stripe_connect_client_id: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  stripe_connect_client_id: PropTypes.string.isRequired,
  setLocale: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const language = state.fetchReducer.user.data
    ? state.fetchReducer.user.data.lang
    : null;

  return {
    language,
  };
};

const mapDispatchToProps = (dispatch) => ({
  set_stripe_publishable_key: (key) => {
    dispatch(setStripeKey(key));
  },
  set_pixel_keys: (ga_key, fb_key) => {
    dispatch(setPixelKeys(ga_key, fb_key));
  },
  set_stripe_connect_client_id: (client_id) => {
    dispatch(setStripeConnectId(client_id));
  },
  setLocale(lang) {
    dispatch(setLocale(lang));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation(["editor-errors", "editor-utils"])(App));
